import { GridViewManager } from './../../core/model/grid-view-manager';
import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, ViewChild } from '@angular/core';
import { DocumentGatewayService } from 'src/app/core/service/document-gateway.service';
import { Subscription } from 'rxjs';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { MessengerService } from 'src/app/core/service/messenger.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { GroupDescriptor, CompositeFilterDescriptor, filterBy } from '@progress/kendo-data-query';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';
import { TranslateService } from '@ngx-translate/core';
import { sortBy } from 'lodash';
import { DocumentViewerComponent } from 'src/app/shared/document-viewer/document-viewer.component';
import { DocumentViewerModel } from 'src/app/shared/document-viewer/model/document-viewer';

@Component({
  standalone: false,
  selector: 'app-clinical-record-list',
  templateUrl: './clinical-record-list.component.html',
  styleUrls: ['./clinical-record-list.component.scss']
})
export class ClinicalRecordListComponent implements OnInit, OnDestroy
{
  @ViewChild('documentViewer') public documentViewer: DocumentViewerComponent;

  @Input() public memberId: number;
  @Output() public refresh: EventEmitter<any> = new EventEmitter();

  public pageSizes = [5, 10, 25, 50, 100];
  public clinicalRecords: any = [];
  public view: GridViewManager;
  public enableAdvanced = false;
  public count: number;
  public filterText = '';
  public lastDocumentQueryDate: Date = null;
  public lastDocumentQuery: any = null;

  public queryErrorTypes: Array<{ text: string; key: string; value: string }> = [
    {
      text: 'Failed registration. Check with the administrator.', key: 'DOCUMENT-QUERY-ERROR-FAILED-REGISTRATION',
      value: 'FAILED_REGISTRATION'
    },
    {
      text: 'Invalid query response. Check with the administrator.', key: 'DOCUMENT-QUERY-ERROR-INVALID-QUERY-RESPONSE',
      value: 'INVALID_QUERY_RESPONSE'
    },
    {
      text: 'Gerenal exception. Check with the administrator.', key: 'DOCUMENT-QUERY-ERROR-GENERAL-EXCEPTION',
      value: 'GENERAL_EXCEPTION'
    },
  ];

  private messagingSubscription: Subscription;

  constructor(
    private documentGatewayService: DocumentGatewayService,
    private appConfigService: AppConfigService,
    private messengerService: MessengerService,
    private messageHandlerService: MessageHandlerService,
    private searchHelperService: SearchHelperService,
    private translate: TranslateService,
  )
  {
    this.view = new GridViewManager(this.appConfigService.getConfig().pageSize);
  }

  public ngOnInit(): void
  {
    const errorTypesTranslationKeys = this.queryErrorTypes.map(a => a.key);
    this.translate.get(errorTypesTranslationKeys).subscribe({
      next: (res: string) =>
      {
        for (let i = 0; i < this.queryErrorTypes.length; i++)
        {
          const key = 'DOCUMENT-QUERY-ERROR-' + this.queryErrorTypes[i].value.replace('_', '-');
          this.queryErrorTypes[i].text = res[key];
        }
        this.queryErrorTypes = sortBy(this.queryErrorTypes, ['text']);
      }
    });

    this.messagingSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'wsDocumentQueryStatusChanged')
        {
          const wsMessage = JSON.parse(message.data.body);
          if (wsMessage !== null && wsMessage.member !== null && wsMessage.member.id === this.memberId)
          {
            switch (wsMessage.status)
            {
              case 'PENDING':
              case 'IN_PROGRESS':
                this.resetGridData();
                break;
              case 'COMPLETED':
              case 'COMPLETED_IN_ERROR':
                this.loadClinicalRecords();
                break;
            }
          }
        }
      }
    });
    this.loadClinicalRecords();
  }

  public ngOnDestroy(): void
  {
    if (this.messagingSubscription !== undefined) { this.messagingSubscription.unsubscribe(); }
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.view.setState(state);
  }

  /**
   * Group change event handler.
   *
   * @param groups List of GroupDescriptors
   */
  public groupChange(groups: GroupDescriptor[]): void
  {
    this.view.setGroup(groups);
  }

  /**
   * Filtering by search field
   */
  public filterChange(filter: CompositeFilterDescriptor): void
  {
    this.view.state.filter = filter;
    this.view.gridData.data = filterBy(this.view.data, filter);
    this.count = this.view.gridData.data.length;
    this.view.refreshView();
  }

  public searchFieldChange(filterText: string): void
  {
    this.view.state.skip = 0;
    const root = this.searchHelperService.fieldChange(filterText, this.view.state);
    this.filterText = filterText;
    this.filterChange(root);
  }

  /**
   * View document handler
   *
   * @param url Document Url
   * @param name Document name
   * @param mediaType Media mime type
   * @param createdOn Document created date
   */
  public viewHandler(id: string, name: string, mediaType: string, createdOn: string): void
  {
    const model = new DocumentViewerModel();
    model.url = `/documents/gateway/data/${id}`;
    model.name = name;
    model.mediaType = mediaType;
    model.createdOn = createdOn;
    model.memberId = this.memberId;

    this.documentViewer.execute(model);
  }

  public getErrorText(errorCode: string): string
  {
    let text = errorCode;
    const error = this.queryErrorTypes.find(x => x.value === errorCode);
    if (error !== undefined)
    {
      text = error.text;
    }
    return text;
  }

  private loadClinicalRecords(): void
  {
    this.view.startLoading();
    this.documentGatewayService.getQueryDocumentResults(this.memberId).subscribe({
      next: (data) =>
      {
        // Keep the last document query id. If we received a WS update with this id, it will be refreshed automatically.
        this.resetGridData();
        if (data !== null && data.documentQuery != null)
        {
          // Set document query data only if completed or completed in error
          switch (data.documentQuery.status)
          {
            case 'COMPLETED':
            case 'COMPLETED_IN_ERROR':
              this.lastDocumentQueryDate = new Date(data.documentQuery.createdOn);
              this.lastDocumentQuery = data.documentQuery;

              data.documents.forEach(d =>
              {
                d.createdDate = d.createdDate !== null ? new Date(d.createdDate) : '';
                d.documentClass = d.classCode !== null ? d.classCode.names : '';
              });
              this.count = data.documents.length;
              this.view.setData(data.documents);
              break;
          }
        }
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      },
      complete: () =>
      {
        this.view.stopLoading();
      }
    });
  }

  private resetGridData(): void
  {
    this.count = 0;
    this.view.setData([]);
    this.lastDocumentQueryDate = null;
    this.lastDocumentQuery = null;
  }
}
